import { clearAuthToken, getAuthToken, getAuthEmail, setAuthToken, getInstallationId, isSignedIn } from './baba-auth'
import { checkoutSessionId, checkoutSignalsBody, getClickSignals, getConsentForApi, getGaClientId, getGaSessionId } from './analytics'

/** Trim and strip optional surrounding quotes — common in .env files and avoids breaking the localhost check. */
function normalizeEnvApiBase(v: string | undefined): string {
  if (v == null || v === '') return ''
  return v.trim().replace(/^["']|["']$/g, '')
}

const rawBabaApiBase = normalizeEnvApiBase(import.meta.env.VITE_BABA_API_BASE_URL as string | undefined)

/** Vite dev server proxies `/api` → local Worker (see vite.config.ts). Match common local API URLs. */
const LOCAL_WORKER_RE = /^https?:\/\/(localhost|127\.0\.0\.1):8787\/?$/i
const PROD_WORKER_RE = /^https:\/\/e\.itsbaba\.com\/?$/i

export const BABA_API_BASE_URL =
  import.meta.env.DEV && (rawBabaApiBase === '' || LOCAL_WORKER_RE.test(rawBabaApiBase) || PROD_WORKER_RE.test(rawBabaApiBase))
    ? ''
    : rawBabaApiBase || 'https://e.itsbaba.com'

function logApiWarning(context: string, err: unknown): void {
  console.warn(`[baba-api] ${context}`, err)
}

function logApiDebug(context: string, err: unknown): void {
  console.debug(`[baba-api] ${context}`, err)
}

function mapGenderContext(v: string | undefined): string | undefined {
  if (!v || v === 'general') return v
  switch (v) {
    case 'to_male': return 'man'
    case 'to_female': return 'woman'
    case 'to_male_group': return 'group-men'
    case 'to_female_group': return 'group-women'
    case 'to_mixed_group': return 'group-men'
    default: return v
  }
}

export type LangCode =
  | 'he' | 'en' | 'fr' | 'ru' | 'uk' | 'es' | 'es-mx' | 'ar'
  | 'de' | 'it' | 'th' | 'sv' | 'zh' | 'ja'

export interface Language {
  code: LangCode
  name: string
  nativeName: string
  rtl?: boolean
}

export const LANGUAGES: Language[] = [
  { code: 'he', name: 'Hebrew', nativeName: 'עברית', rtl: true },
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'fr', name: 'French', nativeName: 'Français' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский' },
  { code: 'uk', name: 'Ukrainian', nativeName: 'Українська' },
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
  { code: 'es-mx', name: 'Spanish (MX)', nativeName: 'Español (MX)' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', rtl: true },
  { code: 'de', name: 'German', nativeName: 'Deutsch' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano' },
  { code: 'th', name: 'Thai', nativeName: 'ไทย' },
  { code: 'sv', name: 'Swedish', nativeName: 'Svenska' },
  { code: 'zh', name: 'Chinese', nativeName: '中文' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語' },
]

/**
 * Normalizes a language value from the shared ledger onto a real `LangCode`.
 *
 * The translations table is written by several clients across several app
 * versions, so `source_language`/`target_language` hold a mix of codes ('he',
 * 'en') and display names ('English', 'Hebrew', 'French'). A display name is
 * not a `LangCode`: `LANGUAGES.find` returns undefined for it, and every
 * caller that reads `.rtl` or `.name` off that result then works on undefined.
 * Normalize at the boundary rather than casting and hoping.
 */
export function toLangCode(value: unknown, fallback: LangCode): LangCode {
  if (typeof value !== 'string') return fallback
  const v = value.trim().toLowerCase()
  if (!v) return fallback
  const match = LANGUAGES.find(
    (l) => l.code === v || l.name.toLowerCase() === v || l.nativeName.toLowerCase() === v,
  )
  return match ? match.code : fallback
}

export type GenderId =
  | 'general' | 'male' | 'female'
  | 'to_male' | 'to_female'
  | 'to_male_group' | 'to_female_group' | 'to_mixed_group'

export const GENDERS: { id: GenderId; label: string }[] = [
  { id: 'general', label: 'No preference' },
  { id: 'male', label: 'Male' },
  { id: 'female', label: 'Female' },
  { id: 'to_male', label: 'Speaking to a man' },
  { id: 'to_female', label: 'Speaking to a woman' },
  { id: 'to_male_group', label: 'Speaking to a group of men' },
  { id: 'to_female_group', label: 'Speaking to a group of women' },
  { id: 'to_mixed_group', label: 'Speaking to a mixed group' },
]

export const SPEAKER_GENDERS: { id: GenderId; label: string }[] = [
  { id: 'male', label: 'Male' },
  { id: 'female', label: 'Female' },
]

export type ModeId = 'standard' | 'slang' | 'summary'
export const MODES: { id: ModeId; label: string; pro: boolean; description: string }[] = [
  { id: 'standard', label: 'Standard', pro: false, description: 'Natural, accurate translation' },
  { id: 'slang', label: 'Slang', pro: true, description: 'Street-level Israeli Hebrew' },
  { id: 'summary', label: 'Summary', pro: true, description: 'Condense long text' },
]

// Everyone gets the smartest model ('balanced'). 'fast' is kept in the type
// only for old history entries that recorded it.
export type ModelId = 'fast' | 'balanced'

const ABSOLUTE_MAX_CHARS = 20000

export interface TranslateOptions {
  text: string
  sourceLang: LangCode
  targetLang: LangCode
  gender?: GenderId
  genderContext?: string
  mode?: ModeId
  model?: ModelId
  context?: string
  style?: string
  /** Opaque gateway job id for an audience-only retranslation. */
  audienceChangeFromId?: string
  /** Opaque apps/api continuation token for an audience-only retranslation. */
  audienceChangeToken?: string
  onToken?: (chunk: string) => void
  onTransliteration?: (text: string) => void
  onUsage?: (usage: UsageSnapshot) => void
  onDone?: (meta: Record<string, unknown>) => void
  onError?: (err: TranslateError) => void
  signal?: AbortSignal
}

export interface UsageSnapshot {
  charsUsed?: number
  charsLimit?: number
  charsPerTranslation?: number
  translationsToday?: number
  translationsLimit?: number
  imagesUsed?: number
  imagesLimit?: number
  pdfsUsed?: number
  pdfsLimit?: number
  documentsUsed?: number
  documentsLimit?: number
  ttsCharsUsed?: number
  ttsCharsLimit?: number | null
  /** Free slang samples left this period (non-Pro tiers), when the API sends it. */
  slangRemaining?: number
  period?: 'monthly' | 'yearly'
  plan?: 'monthly' | 'yearly' | null
  /**
   * Which ledger the numbers came from. 'gateway' = the baba-v2 mobile
   * `users` row a linked account spends on from every surface (all counters
   * share the text window and reset together at `resetsAt`); 'web' (or
   * absent) = apps/api's own extension_* tables, where characters follow the
   * plan period but images/documents are always calendar-month.
   */
  ledger?: 'gateway' | 'web'
  /** ISO timestamp the gateway window rolls over (gateway ledger only). */
  resetsAt?: string
}

export type TranslateError =
  | { type: 'limit_reached'; message?: string; upgrade_url?: string }
  | { type: 'slang_limit'; message?: string }
  | { type: 'error'; message: string }

function parseUsageHeaders(response: Response): UsageSnapshot {
  const h = response.headers
  const n = (k: string): number | undefined => {
    const v = h.get(k)
    if (v == null) return undefined
    const parsed = parseInt(v, 10)
    return Number.isFinite(parsed) ? parsed : undefined
  }
  const period = h.get('X-Usage-Period')
  const plan = h.get('X-User-Plan')
  return {
    charsUsed: n('X-Usage-Chars-Used'),
    charsLimit: n('X-Usage-Chars-Limit'),
    translationsToday: n('X-Usage-Translations-Today'),
    translationsLimit: n('X-Usage-Translations-Limit'),
    slangRemaining: n('X-Usage-Slang-Remaining'),
    period: period === 'yearly' ? 'yearly' : period === 'monthly' ? 'monthly' : undefined,
    plan: plan === 'monthly' || plan === 'yearly' ? plan : null,
  }
}

export async function translateStream(opts: TranslateOptions): Promise<void> {
  const {
    text,
    sourceLang,
    targetLang,
    gender = 'general',
    genderContext,
    mode = 'standard',
    model = 'balanced',
    context,
    style,
    audienceChangeToken,
    signal,
  } = opts

  if (!text || text.length > ABSOLUTE_MAX_CHARS) {
    opts.onError?.({ type: 'error', message: 'Text exceeds maximum length' })
    return
  }

  // "One ledger": a signed-in user's text translations run on the baba-v2
  // mobile gateway's shared character ledger/history instead of apps/api's
  // separate guest/free quota, once the gateway is confirmed reachable for
  // this session (the shared probe in ./library/useLibrarySource.ts — the
  // SAME check the Library panels use). Dynamically imported so a signed-out
  // caller (and every existing apps/api-only test of this file) never pulls
  // in the gateway modules at all. `translateGateway/client.ts` guarantees
  // it has called neither onToken nor onError before returning 'unavailable',
  // so falling through below is exactly as if this attempt never happened.
  if (isSignedIn()) {
    try {
      const [{ translateViaGateway, shouldRouteThroughGateway }, { ensureGatewaySourceProbe }] = await Promise.all([
        import('./translateGateway/client'),
        import('./library/useLibrarySource'),
      ])
      const gatewayStatus = await ensureGatewaySourceProbe()
      if (shouldRouteThroughGateway({ signedIn: true, gatewayStatus })) {
        const result = await translateViaGateway(opts)
        if (result.status === 'ok') return
      }
    } catch (err) {
      logApiDebug('translateStream: gateway routing failed, falling back to apps/api', err)
    }
  }

  // Set when the silence limit below (not the caller) ended the request.
  let timedOut = false
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      'X-Installation-ID': getInstallationId(),
    }
    const token = getAuthToken()
    if (token) headers['Authorization'] = `Bearer ${token}`

    // A silence limit, not a total one: restarted on every chunk below, so a
    // long Pro translation that keeps streaming is never cut off (see
    // STREAM_IDLE_TIMEOUT_MS in translateGateway/client.ts, same value).
    const timeoutCtrl = new AbortController()
    const startSilenceClock = () =>
      setTimeout(() => {
        timedOut = true
        timeoutCtrl.abort()
      }, 60_000)
    let timeoutId = startSilenceClock()
    if (signal) {
      if (signal.aborted) timeoutCtrl.abort()
      else signal.addEventListener('abort', () => timeoutCtrl.abort(), { once: true })
    }
    const combinedSignal = timeoutCtrl.signal

    const response = await fetch(`${BABA_API_BASE_URL}/api/extension/translate`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        text,
        source_lang: sourceLang,
        target_lang: targetLang,
        gender,
        genderContext: mapGenderContext(genderContext),
        mode,
        model,
        context: context?.slice(0, ABSOLUTE_MAX_CHARS),
        style: style || undefined,
        audienceChangeToken,
        // The translator pane shows a Hebrew-letter pronunciation under a
        // foreign result; the API only writes one for clients that ask.
        hebrew_pronunciation: true,
      }),
      signal: combinedSignal,
    })

    if (response.status === 429) {
      clearTimeout(timeoutId)
      const data = await response.json().catch(() => ({}))
      opts.onError?.({ type: 'limit_reached', ...data })
      return
    }

    if (!response.ok) {
      clearTimeout(timeoutId)
      const data = await response.json().catch(() => ({}))
      // Free slang samples exhausted (anonymous: 1/period, free: 3/period).
      // Distinct from the character quota so the UI can pitch slang on Pro.
      if (response.status === 403 && data?.code === 'slang_limit') {
        opts.onError?.({ type: 'slang_limit', message: data.message || data.error })
        return
      }
      opts.onError?.({ type: 'error', message: data.message || data.error || friendlyError(null, response.status) })
      return
    }

    opts.onUsage?.(parseUsageHeaders(response))

    if (!response.body) {
      clearTimeout(timeoutId)
      opts.onError?.({ type: 'error', message: 'No response body from server' })
      return
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    let receivedDone = false
    let streamAborted = false
    const TRANSLIT_SEPARATOR = '∰'
    let inTransliteration = false

    try {
      for (;;) {
        let chunk: ReadableStreamReadResult<Uint8Array>
        try {
          chunk = await reader.read()
        } catch (readErr) {
          if ((readErr as Error).name === 'AbortError') streamAborted = true
          throw readErr
        }
        const { done, value } = chunk
        if (done) break
        clearTimeout(timeoutId)
        timeoutId = startSilenceClock()
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          try {
            const data = JSON.parse(line.slice(6))
            switch (data.type) {
              case 'token': {
                const text: string = data.text || ''
                if (!text) break
                if (text.includes(TRANSLIT_SEPARATOR)) {
                  const [before, ...rest] = text.split(TRANSLIT_SEPARATOR)
                  const after = rest.join(TRANSLIT_SEPARATOR)
                  const beforeTrim = before.replace(/\s+$/, '')
                  if (beforeTrim) {
                    if (inTransliteration) opts.onTransliteration?.(beforeTrim)
                    else opts.onToken?.(beforeTrim)
                  }
                  inTransliteration = true
                  const afterTrim = after.replace(/^\s+/, '')
                  if (afterTrim) opts.onTransliteration?.(afterTrim)
                } else if (inTransliteration) {
                  opts.onTransliteration?.(text)
                } else {
                  opts.onToken?.(text)
                }
                break
              }
              case 'transliteration':
                opts.onTransliteration?.(data.text)
                break
              case 'done':
                receivedDone = true
                opts.onDone?.(data)
                break
              case 'error':
                if (data.code === 'slang_limit') {
                  opts.onError?.({ type: 'slang_limit', message: data.message || data.error })
                } else {
                  opts.onError?.({ type: 'error', message: data.message || 'Translation failed' })
                }
                break
            }
          } catch (err) {
            logApiDebug('translateStream: malformed SSE line', err)
          }
        }
      }
    } finally {
      clearTimeout(timeoutId)
      // A stream cut short (cancelled, or silent past the limit) did not
      // finish: never report it done, or its partial text is saved as a
      // complete translation.
      if (!receivedDone && !streamAborted) opts.onDone?.({})
    }
  } catch (err) {
    const e = err as Error
    if (e.name === 'AbortError') {
      if (timedOut && !signal?.aborted) {
        opts.onError?.({ type: 'error', message: 'The translation timed out before it finished. Try again.' })
      }
      return
    }
    logApiWarning('translateStream', err)
    opts.onError?.({ type: 'error', message: friendlyError(e) })
  }
}

function friendlyError(err: Error | null, status?: number): string {
  if (status && status >= 500) return 'baba is having a moment. Please try again.'
  if (err && (err.name === 'TypeError' || /network|failed to fetch/i.test(err.message))) {
    return "Can't reach baba right now. Check your connection and try again."
  }
  return err?.message || 'Translation failed'
}

/**
 * After a media translation lands on the shared gateway ledger, re-probe the
 * Library source (the same module-level probe every Library panel reads) so
 * the new row shows up immediately instead of on the next reload. Best-effort
 * and fire-and-forget: the translation itself has already succeeded, and the
 * row exists on the server either way.
 */
function refreshLibraryAfterGatewayMedia(historyId: string | undefined): void {
  if (!historyId) return
  void import('./library/useLibrarySource')
    .then((m) => m.refreshGatewaySource())
    .catch((err) => logApiDebug('refreshGatewaySource after a gateway media translation failed', err))
}

export type PdfMode = 'translate' | 'summarize' | 'full'

export interface PdfTranslateOptions {
  pdfBase64: string
  targetLanguage: string
  mode?: PdfMode
  context?: string
  filename?: string
  pageCount?: number
  gender?: GenderId
  /** The picked target language CODE. Used only by the "one ledger" gateway
   *  path (`./translateGateway/mediaClient.ts`) to record the row's direction
   *  the way the phone does; the apps/api path ignores it. */
  targetLang?: LangCode
  onToken?: (chunk: string) => void
  onUsage?: (usage: UsageSnapshot) => void
  onDone?: (meta: Record<string, unknown>) => void
  onError?: (err: TranslateError) => void
  onStatus?: (status: 'processing' | 'translating') => void
  signal?: AbortSignal
}

const PDF_MAX_BYTES = 10 * 1024 * 1024
const PDF_MAX_PAGES = 20

export async function translatePdfStream(opts: PdfTranslateOptions): Promise<void> {
  const { pdfBase64, targetLanguage, mode = 'translate', context, filename, pageCount, gender, signal } = opts

  if (!pdfBase64) {
    opts.onError?.({ type: 'error', message: 'No PDF provided' })
    return
  }
  if (pdfBase64.length * 0.75 > PDF_MAX_BYTES) {
    opts.onError?.({ type: 'error', message: 'PDF is larger than 10 MB' })
    return
  }
  if (pageCount && pageCount > PDF_MAX_PAGES) {
    opts.onError?.({ type: 'error', message: `PDF has too many pages (${pageCount}). Max is ${PDF_MAX_PAGES}.` })
    return
  }

  // "One ledger" (see the image path below and ./translateGateway/mediaClient.ts).
  if (isSignedIn()) {
    try {
      const [{ translatePdfViaGateway }, { ensureGatewaySourceProbe }, { shouldRouteThroughGateway }] =
        await Promise.all([
          import('./translateGateway/mediaClient'),
          import('./library/useLibrarySource'),
          import('./translateGateway/client'),
        ])
      const gatewayStatus = await ensureGatewaySourceProbe()
      if (shouldRouteThroughGateway({ signedIn: true, gatewayStatus })) {
        const result = await translatePdfViaGateway(opts)
        if (result.status === 'ok') {
          refreshLibraryAfterGatewayMedia(result.historyId)
          return
        }
      }
    } catch (err) {
      logApiDebug('translatePdfStream: gateway routing failed, falling back to apps/api', err)
    }
  }

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      'X-Installation-ID': getInstallationId(),
    }
    const token = getAuthToken()
    if (token) headers['Authorization'] = `Bearer ${token}`

    const timeoutCtrl = new AbortController()
    const timeoutId = setTimeout(() => timeoutCtrl.abort(), 30_000)
    if (signal) {
      if (signal.aborted) timeoutCtrl.abort()
      else signal.addEventListener('abort', () => timeoutCtrl.abort(), { once: true })
    }

    const response = await fetch(`${BABA_API_BASE_URL}/api/extension/translate-pdf`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        pdfBase64,
        targetLanguage,
        mode,
        context: context?.slice(0, ABSOLUTE_MAX_CHARS),
        filename,
        pageCount,
        gender,
      }),
      signal: timeoutCtrl.signal,
    })
    clearTimeout(timeoutId)

    if (response.status === 429) {
      clearTimeout(timeoutId)
      const data = await response.json().catch(() => ({}))
      opts.onError?.({ type: 'limit_reached', ...data })
      return
    }
    if (!response.ok) {
      clearTimeout(timeoutId)
      const data = await response.json().catch(() => ({}))
      opts.onError?.({ type: 'error', message: data.message || data.error || friendlyError(null, response.status) })
      return
    }

    opts.onUsage?.(parseUsageHeaders(response))
    if (!response.body) {
      clearTimeout(timeoutId)
      opts.onError?.({ type: 'error', message: 'No response body from server' })
      return
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    let receivedDone = false
    try {
      for (;;) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          try {
            const data = JSON.parse(line.slice(6))
            switch (data.type) {
              case 'token':
                if (data.text) opts.onToken?.(data.text)
                break
              case 'status':
                if (data.status === 'processing' || data.status === 'translating') {
                  opts.onStatus?.(data.status)
                }
                break
              case 'done':
                receivedDone = true
                opts.onDone?.(data)
                break
              case 'error':
                opts.onError?.({ type: 'error', message: data.message || 'PDF translation failed' })
                break
            }
          } catch (err) {
            logApiDebug('translatePdfStream: malformed SSE line', err)
          }
        }
      }
    } finally {
      clearTimeout(timeoutId)
      if (!receivedDone) opts.onDone?.({})
    }
  } catch (err) {
    const e = err as Error
    if (e.name === 'AbortError') return
    logApiWarning('translatePdfStream', err)
    opts.onError?.({ type: 'error', message: friendlyError(e) })
  }
}

export type DocMode = 'translate' | 'summarize' | 'full'

export interface DocTranslateOptions {
  fileBase64: string
  fileType: 'docx' | 'pptx'
  filename?: string
  targetLanguage: string
  mode?: DocMode
  gender?: GenderId
  onToken?: (chunk: string) => void
  onUsage?: (usage: UsageSnapshot) => void
  onDone?: (meta: Record<string, unknown>) => void
  onError?: (err: TranslateError) => void
  onStatus?: (status: 'processing' | 'translating') => void
  signal?: AbortSignal
}

const DOC_MAX_BYTES = 10 * 1024 * 1024

/**
 * Word and PowerPoint translation stays on apps/api, deliberately — it is NOT
 * an oversight next to the image and PDF paths above. The baba-v2 gateway has
 * no document pipeline (only `/translate`, `/translate-image` and
 * `/translate-pdf` are mounted) and the phone has no document feature at all,
 * so there is no shared ledger row for a .docx/.pptx translation to land in
 * and nothing on mobile that could display one. If a gateway document
 * pipeline ever ships, this is the function that routes to it.
 */
export async function translateDocStream(opts: DocTranslateOptions): Promise<void> {
  const { fileBase64, fileType, filename, targetLanguage, mode = 'translate', gender, signal } = opts

  if (!fileBase64) {
    opts.onError?.({ type: 'error', message: 'No document provided' })
    return
  }
  if (fileBase64.length * 0.75 > DOC_MAX_BYTES) {
    opts.onError?.({ type: 'error', message: 'Document is larger than 10 MB' })
    return
  }

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      'X-Installation-ID': getInstallationId(),
    }
    const token = getAuthToken()
    if (token) headers['Authorization'] = `Bearer ${token}`

    const timeoutCtrl = new AbortController()
    const timeoutId = setTimeout(() => timeoutCtrl.abort(), 30_000)
    if (signal) {
      if (signal.aborted) timeoutCtrl.abort()
      else signal.addEventListener('abort', () => timeoutCtrl.abort(), { once: true })
    }

    const response = await fetch(`${BABA_API_BASE_URL}/api/extension/translate-doc`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        fileBase64,
        fileType,
        filename,
        targetLanguage,
        mode,
        gender,
      }),
      signal: timeoutCtrl.signal,
    })
    clearTimeout(timeoutId)

    if (response.status === 429) {
      clearTimeout(timeoutId)
      const data = await response.json().catch(() => ({}))
      opts.onError?.({ type: 'limit_reached', ...data })
      return
    }
    if (!response.ok) {
      clearTimeout(timeoutId)
      const data = await response.json().catch(() => ({}))
      opts.onError?.({ type: 'error', message: data.message || data.error || friendlyError(null, response.status) })
      return
    }

    opts.onUsage?.(parseUsageHeaders(response))
    if (!response.body) {
      clearTimeout(timeoutId)
      opts.onError?.({ type: 'error', message: 'No response body from server' })
      return
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    let receivedDone = false
    try {
      for (;;) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          try {
            const data = JSON.parse(line.slice(6))
            switch (data.type) {
              case 'token':
                if (data.text) opts.onToken?.(data.text)
                break
              case 'status':
                if (data.status === 'processing' || data.status === 'translating') {
                  opts.onStatus?.(data.status)
                }
                break
              case 'done':
                receivedDone = true
                opts.onDone?.(data)
                break
              case 'error':
                opts.onError?.({ type: 'error', message: data.message || 'Document translation failed' })
                break
            }
          } catch (err) {
            logApiDebug('translateDocStream: malformed SSE line', err)
          }
        }
      }
    } finally {
      clearTimeout(timeoutId)
      if (!receivedDone) opts.onDone?.({})
    }
  } catch (err) {
    const e = err as Error
    if (e.name === 'AbortError') return
    logApiWarning('translateDocStream', err)
    opts.onError?.({ type: 'error', message: friendlyError(e) })
  }
}

export type ImageMode = 'summary' | 'word-for-word' | 'full'
export type ImageModel = 'standard' | 'deep'

export interface ImageTranslateOptions {
  imageBase64: string
  language: string
  mode?: ImageMode
  model?: ImageModel
  detailLevel?: 'high' | 'low'
  context?: string
  gender?: GenderId
  timeContext?: string
  /** The picked target language CODE — see `PdfTranslateOptions.targetLang`. */
  targetLang?: LangCode
  onToken?: (chunk: string) => void
  onUsage?: (usage: UsageSnapshot) => void
  onDone?: (meta: Record<string, unknown>) => void
  onError?: (err: TranslateError) => void
  /** Pipeline stage progress: 'extracting' → OCR, 'translating' → LLM. */
  onStatus?: (status: 'extracting' | 'translating') => void
  /** Fired once Datalab OCR completes, before the LLM translation streams. */
  onExtracted?: (extractedHebrew: string, ocrLatencyMs: number) => void
  /** Gateway only: the result card while it is still being written (raw,
   *  incomplete `partialStructured` object, ~every 200ms). Display only —
   *  `onDone` is still the one completion signal. */
  onPartialStructured?: (partial: Record<string, unknown>) => void
  signal?: AbortSignal
}

export async function translateImageStream(opts: ImageTranslateOptions): Promise<void> {
  const { imageBase64, language, mode = 'summary', model = 'standard', detailLevel = 'high', context, gender, timeContext, signal } = opts

  if (!imageBase64) {
    opts.onError?.({ type: 'error', message: 'No image provided' })
    return
  }

  // "One ledger": a signed-in user's image translations run on the baba-v2
  // mobile gateway — the same monthly image allowance and the same
  // `translations` rows (content_type 'image', with a persisted `image_url`)
  // the phone writes — instead of apps/api, which persists nothing and could
  // therefore never show a web-translated image on the phone. Gated on the
  // SAME shared probe the text path and the Library panels use. Dynamically
  // imported so a signed-out caller never pulls the gateway modules in.
  // `mediaClient.ts` guarantees it has called neither onToken nor onError
  // before returning 'unavailable', so falling through is as if it never ran.
  if (isSignedIn()) {
    try {
      const [{ translateImageViaGateway }, { ensureGatewaySourceProbe }, { shouldRouteThroughGateway }] =
        await Promise.all([
          import('./translateGateway/mediaClient'),
          import('./library/useLibrarySource'),
          import('./translateGateway/client'),
        ])
      const gatewayStatus = await ensureGatewaySourceProbe()
      if (shouldRouteThroughGateway({ signedIn: true, gatewayStatus })) {
        const result = await translateImageViaGateway(opts)
        if (result.status === 'ok') {
          refreshLibraryAfterGatewayMedia(result.historyId)
          return
        }
      }
    } catch (err) {
      logApiDebug('translateImageStream: gateway routing failed, falling back to apps/api', err)
    }
  }

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      'X-Installation-ID': getInstallationId(),
    }
    const token = getAuthToken()
    if (token) headers['Authorization'] = `Bearer ${token}`

    const timeoutCtrl = new AbortController()
    const timeoutId = setTimeout(() => timeoutCtrl.abort(), 30_000)
    if (signal) {
      if (signal.aborted) timeoutCtrl.abort()
      else signal.addEventListener('abort', () => timeoutCtrl.abort(), { once: true })
    }

    const response = await fetch(`${BABA_API_BASE_URL}/api/extension/translate-image`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        imageBase64,
        language,
        mode,
        model,
        detailLevel,
        context: context?.slice(0, ABSOLUTE_MAX_CHARS),
        gender,
        timeContext,
      }),
      signal: timeoutCtrl.signal,
    })
    clearTimeout(timeoutId)

    if (response.status === 429) {
      clearTimeout(timeoutId)
      const data = await response.json().catch(() => ({}))
      opts.onError?.({ type: 'limit_reached', ...data })
      return
    }
    if (!response.ok) {
      clearTimeout(timeoutId)
      const data = await response.json().catch(() => ({}))
      opts.onError?.({ type: 'error', message: data.message || data.error || friendlyError(null, response.status) })
      return
    }

    opts.onUsage?.(parseUsageHeaders(response))
    if (!response.body) {
      clearTimeout(timeoutId)
      opts.onError?.({ type: 'error', message: 'No response body from server' })
      return
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    let receivedDone = false
    try {
      for (;;) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          try {
            const data = JSON.parse(line.slice(6))
            switch (data.type) {
              case 'token':
                if (data.text) opts.onToken?.(data.text)
                break
              case 'status':
                if (data.status === 'extracting' || data.status === 'translating') {
                  opts.onStatus?.(data.status)
                }
                break
              case 'extracted':
                opts.onExtracted?.(
                  typeof data.extractedHebrew === 'string' ? data.extractedHebrew : '',
                  typeof data.ocrLatencyMs === 'number' ? data.ocrLatencyMs : 0,
                )
                break
              case 'done':
                receivedDone = true
                opts.onDone?.(data)
                break
              case 'error':
                opts.onError?.({ type: 'error', message: data.message || 'Image translation failed' })
                break
            }
          } catch (err) {
            logApiDebug('translateImageStream: malformed SSE line', err)
          }
        }
      }
    } finally {
      clearTimeout(timeoutId)
      if (!receivedDone) opts.onDone?.({})
    }
  } catch (err) {
    const e = err as Error
    if (e.name === 'AbortError') return
    logApiWarning('translateImageStream', err)
    opts.onError?.({ type: 'error', message: friendlyError(e) })
  }
}

export type VoiceGender = 'male' | 'female'

export interface GenerateSpeechOptions {
  text: string
  voiceGender: VoiceGender
  lang: LangCode
  signal?: AbortSignal
}

export interface GenerateSpeechResult {
  audioUrl: string
  charactersUsed: number
}

const TTS_PER_REQUEST_CAP = 300

// Replaying the same text shouldn't re-hit ElevenLabs (or re-charge the
// user's character quota) — cache generated audio per text+voice+lang.
// Callers revoke the object URLs they receive, so cache the underlying
// Blob and mint a fresh URL per call.
const speechCache = new Map<string, Blob>()
const SPEECH_CACHE_MAX = 20

export async function generateSpeech(opts: GenerateSpeechOptions): Promise<GenerateSpeechResult> {
  const { text, voiceGender, lang, signal } = opts
  const trimmed = (text || '').slice(0, TTS_PER_REQUEST_CAP)
  if (!trimmed) throw new Error('No text to speak')

  const cacheKey = `${voiceGender}|${lang}|${trimmed}`
  const cached = speechCache.get(cacheKey)
  if (cached) {
    // Refresh LRU position so frequently replayed audio stays cached.
    speechCache.delete(cacheKey)
    speechCache.set(cacheKey, cached)
    return { audioUrl: URL.createObjectURL(cached), charactersUsed: 0 }
  }

  // "One ledger" (same routing as translateStream above): a signed-in session
  // whose translations run on the gateway plays voice there too, so the
  // phone's `tts_usage_characters` is the only listening counter a linked
  // account has. The gateway answers with a public clip URL it caches
  // server-side per translationId, so this path never touches speechCache.
  // `translateGateway/ttsClient.ts` returns 'unavailable' (nothing charged)
  // for 401 / 403 / 5xx, and THROWS for a settled answer from the same
  // ledger (limit, user cancel) or an ambiguous dispatched-but-unanswered
  // request (`type: 'retry'`) — those must surface, never fall back to
  // apps/api's separate quota (the second would double-charge one click).
  if (isSignedIn()) {
    try {
      const [{ speakViaGateway }, { shouldRouteThroughGateway }, { ensureGatewaySourceProbe }] = await Promise.all([
        import('./translateGateway/ttsClient'),
        import('./translateGateway/client'),
        import('./library/useLibrarySource'),
      ])
      const gatewayStatus = await ensureGatewaySourceProbe()
      if (shouldRouteThroughGateway({ signedIn: true, gatewayStatus })) {
        const outcome = await speakViaGateway({ text: trimmed, voiceGender, lang, signal })
        if (outcome.status === 'ok') return outcome.result
      }
    } catch (err) {
      const e = err as Error & { type?: string }
      if (e.type === 'limit_reached' || e.type === 'pro_required' || e.type === 'retry' || e.name === 'AbortError') throw err
      logApiDebug('generateSpeech: gateway routing failed, falling back to apps/api', err)
    }
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Installation-ID': getInstallationId(),
  }
  const token = getAuthToken()
  if (token) headers['Authorization'] = `Bearer ${token}`

  const response = await fetch(`${BABA_API_BASE_URL}/api/extension/tts`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ text: trimmed, voiceGender, lang }),
    signal,
  })

  if (response.status === 401) {
    // Stale session: drop the dead token so the UI can re-prompt sign-in.
    clearAuthToken({ keepEmail: true })
    throw Object.assign(new Error('Sign in required to use text-to-speech'), { type: 'auth_required' })
  }
  if (response.status === 403) {
    const data = await response.json().catch(() => ({}))
    const msg = data?.message || 'Voice playback is a Pro feature.'
    throw Object.assign(new Error(msg), { type: 'pro_required' })
  }
  if (response.status === 429) {
    const data = await response.json().catch(() => ({}))
    const msg = data?.message || 'TTS limit reached'
    throw Object.assign(new Error(msg), { type: 'limit_reached' })
  }
  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error(data?.error || `TTS failed (${response.status})`)
  }

  const blob = await response.blob()
  speechCache.set(cacheKey, blob)
  if (speechCache.size > SPEECH_CACHE_MAX) {
    const oldest = speechCache.keys().next().value
    if (oldest !== undefined) speechCache.delete(oldest)
  }
  const audioUrl = URL.createObjectURL(blob)
  return { audioUrl, charactersUsed: trimmed.length }
}

export interface ChatConversation {
  id: string
  title: string | null
  created_at: string
  updated_at: string
  last_message_preview: string | null
  language_pair?: { a: string; b: string }
  language_a?: string
  language_b?: string
  settings?: Record<string, unknown>
  message_count: number
  archived_at: string | null
}

export interface ChatMessage {
  id: string
  conversation_id: string
  role: 'user' | 'assistant'
  status: 'pending' | 'streaming' | 'complete' | 'partial' | 'failed' | 'cancelled' | 'interrupted' | 'superseded'
  source_text: string | null
  translated_text: string | null
  detected_source_lang: string | null
  target_lang: string | null
  gender?: string | null
  mode?: string | null
  model?: string | null
  style?: string | null
  attempt_of?: string | null
  error_message?: string | null
  attachments?: Array<{
    type?: string
    filename?: string
    mimeType?: string | null
    size?: number | null
    base64?: string
  }>
  created_at: string
  updated_at?: string
  optimistic?: boolean
}

function authHeaders(json = true): Record<string, string> {
  const headers: Record<string, string> = {
    'X-Installation-ID': getInstallationId(),
  }
  if (json) headers['Content-Type'] = 'application/json'
  const token = getAuthToken()
  if (token) headers.Authorization = `Bearer ${token}`
  return headers
}

async function readJsonOrThrow<T>(response: Response): Promise<T> {
  const data = await response.json().catch(() => ({}))
  if (response.status === 401) clearAuthToken({ keepEmail: true })
  if (!response.ok) throw new Error(data?.message || data?.error || `Request failed (${response.status})`)
  return data as T
}

export async function fetchChatConversations(params?: { cursor?: string; limit?: number; archived?: boolean }): Promise<{ conversations: ChatConversation[]; nextCursor: string | null }> {
  const query = new URLSearchParams()
  if (params?.cursor) query.set('cursor', params.cursor)
  if (params?.limit) query.set('limit', String(params.limit))
  if (params?.archived) query.set('archived', 'true')
  const suffix = query.toString() ? `?${query}` : ''
  const response = await fetch(`${BABA_API_BASE_URL}/api/extension/chat/conversations${suffix}`, {
    headers: authHeaders(false),
  })
  return readJsonOrThrow(response)
}

export async function fetchChatConversation(id: string, options: { signal?: AbortSignal } = {}): Promise<{ conversation: ChatConversation; messages: ChatMessage[] }> {
  const response = await fetch(`${BABA_API_BASE_URL}/api/extension/chat/conversations/${encodeURIComponent(id)}`, {
    headers: authHeaders(false),
    signal: options.signal,
  })
  return readJsonOrThrow(response)
}

export async function patchChatConversation(id: string, body: Record<string, unknown>): Promise<{ conversation: ChatConversation }> {
  const response = await fetch(`${BABA_API_BASE_URL}/api/extension/chat/conversations/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: authHeaders(true),
    body: JSON.stringify(body),
  })
  return readJsonOrThrow(response)
}

export async function deleteChatConversation(id: string): Promise<void> {
  const response = await fetch(`${BABA_API_BASE_URL}/api/extension/chat/conversations/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: authHeaders(false),
  })
  await readJsonOrThrow(response)
}

export async function createChatShare(id: string): Promise<{ token: string; url: string; createdAt: string }> {
  const response = await fetch(`${BABA_API_BASE_URL}/api/extension/chat/conversations/${encodeURIComponent(id)}/share`, {
    method: 'POST',
    headers: authHeaders(false),
  })
  return readJsonOrThrow(response)
}

export async function deleteChatShare(id: string): Promise<void> {
  const response = await fetch(`${BABA_API_BASE_URL}/api/extension/chat/conversations/${encodeURIComponent(id)}/share`, {
    method: 'DELETE',
    headers: authHeaders(false),
  })
  await readJsonOrThrow(response)
}

export async function fetchSharedChat(token: string): Promise<{ conversation: ChatConversation; messages: ChatMessage[] }> {
  const response = await fetch(`${BABA_API_BASE_URL}/api/extension/chat/share/${encodeURIComponent(token)}`)
  return readJsonOrThrow(response)
}

export interface PricingTier {
  price: number
  currency: string
  display?: string
  stripe_price_id?: string
  monthly_equivalent?: number
  savings_percent?: number
}

export interface PricingInfo {
  monthly?: PricingTier
  yearly?: PricingTier
  /** Only present when Isaac has created the Pro+ prices in Stripe; null otherwise. */
  proPlus?: { monthly: PricingTier; yearly: PricingTier } | null
  currency?: string
  promo_discount_percent?: number
}

/** One entry per subscription source actually on the account — see
 * apps/api's `listEntitlementSources`. Additive: never used to decide
 * is_pro/managed_by, only to render the "You Have Two Active Subscriptions"
 * card in Settings when more than one entry is currently active. */
export interface SubscriptionSource {
  provider: 'stripe' | 'mobile_revenuecat'
  status: string
  tier: 'free' | 'pro' | 'pro_plus'
  current_period_end: string | null
}

export interface SubscriptionStatus {
  status: 'free' | 'active' | 'canceled' | 'past_due' | 'unknown' | string
  plan: 'monthly' | 'yearly' | null
  is_pro?: boolean
  /** Paid tier when is_pro is true; undefined means not yet known (e.g. not polled yet). */
  tier?: 'free' | 'pro' | 'pro_plus'
  email?: string
  current_period_end?: number
  authExpired?: boolean
  fetchFailed?: boolean
  /** Who administers this subscription. Undefined means unknown (e.g. not yet polled) — treat as neither Stripe nor mobile. */
  managed_by?: 'stripe' | 'mobile' | 'none'
  /** Per-source breakdown, when the API reported one. Undefined (not yet
   * polled) and [] (nothing on the account) both mean "don't show the card". */
  sources?: SubscriptionSource[]
}

const LIVE_SOURCE_STATUSES = new Set(['active', 'trialing', 'past_due'])

/** True when this source's own status currently counts as a real, billing
 * subscription — same active/trialing/past_due set apps/api's
 * `isLiveSubscriptionStatus` uses server-side. */
export function isSourceActive(source: Pick<SubscriptionSource, 'status' | 'current_period_end'>): boolean {
  if (!LIVE_SOURCE_STATUSES.has(source.status)) return false
  if (!source.current_period_end) return true
  const t = Date.parse(source.current_period_end)
  return Number.isFinite(t) && t > Date.now()
}

/** True only for an active/pro subscriber whose tier is specifically Pro+. */
export function isProPlusActive(status: Pick<SubscriptionStatus, 'status' | 'is_pro' | 'tier'> | null | undefined): boolean {
  return isSubscriptionActive(status) && status?.tier === 'pro_plus'
}

const SUBSCRIPTION_CACHE_KEY = 'baba.app.last_subscription_status'

function cacheOwner(): string {
  return (getAuthEmail() ?? '').trim().toLowerCase()
}

/**
 * Last subscription status this browser saw FOR THE ACCOUNT NOW SIGNED IN,
 * read synchronously from storage. Stored with the email it belongs to, and
 * ignored for anyone else: it seeds the plan on first paint (main.tsx), and
 * another account's "Pro" must never be what a new session starts from.
 */
export function readCachedSubscription(): SubscriptionStatus | null {
  try {
    const raw = typeof localStorage !== 'undefined'
      ? localStorage.getItem(SUBSCRIPTION_CACHE_KEY)
      : null
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || !('status' in parsed)) return null
    const { owner, ...status } = parsed as SubscriptionStatus & { owner?: unknown }
    if (typeof owner !== 'string' || !owner || owner !== cacheOwner()) return null
    return status
  } catch (err) {
    logApiWarning('readCachedSubscription', err)
  }
  return null
}

function writeCachedSubscription(s: SubscriptionStatus): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(SUBSCRIPTION_CACHE_KEY, JSON.stringify({ ...s, owner: cacheOwner() }))
    }
  } catch (err) {
    logApiWarning('writeCachedSubscription', err)
  }
}

export interface SubscriptionRestoreResult {
  restored: boolean
  already_active?: boolean
  plan?: 'monthly' | 'yearly' | null
  current_period_end?: string
  message?: string
  error?: string
}

export const SUBSCRIPTION_UPDATED_EVENT = 'baba:subscription-updated'

export function isSubscriptionActive(status: Pick<SubscriptionStatus, 'status' | 'is_pro'> | null | undefined): boolean {
  return status?.status === 'active' || status?.is_pro === true
}

export function broadcastSubscriptionStatus(status: SubscriptionStatus): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(SUBSCRIPTION_UPDATED_EVENT, { detail: status }))
}

// Seed subscription cache from auth verification payload so the UI can reflect
// Pro immediately even if the first subscription-status fetch is delayed/fails.
export function seedSubscriptionFromAuth(result: {
  subscription_status?: string | null
  plan?: 'monthly' | 'yearly' | null | string
}): SubscriptionStatus {
  const status = (result.subscription_status ?? 'free') as SubscriptionStatus['status']
  const plan = result.plan === 'monthly' || result.plan === 'yearly' ? result.plan : null
  const seeded: SubscriptionStatus = {
    status,
    plan,
    is_pro: status === 'active',
  }
  writeCachedSubscription(seeded)
  return seeded
}

async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers)
  headers.set('X-Installation-ID', getInstallationId())
  if (!headers.has('Content-Type') && init.body) headers.set('Content-Type', 'application/json')
  const token = getAuthToken()
  if (token) headers.set('Authorization', `Bearer ${token}`)
  return fetch(`${BABA_API_BASE_URL}${path}`, { ...init, headers })
}

export interface AccountLinkCode {
  code: string
  expires_at: string
  instructions: string
}

export async function createAccountLinkCode(): Promise<AccountLinkCode> {
  const response = await authedFetch('/api/extension/account-link/code', {
    method: 'POST',
  })
  if (!response.ok) {
    throw new Error(response.status === 401 ? 'Please sign in again.' : 'Could not create a link code.')
  }
  return response.json()
}

export async function fetchAccountLinkStatus(): Promise<{ linked: boolean }> {
  const response = await authedFetch('/api/extension/account-link/status')
  if (!response.ok) throw new Error('Could not check account link status.')
  return response.json()
}

/** Detaches a previously-linked mobile app from this web account (web-initiated, POST /account-link/unlink). */
export async function unlinkAccount(): Promise<{ unlinked: boolean }> {
  const response = await authedFetch('/api/extension/account-link/unlink', {
    method: 'POST',
  })
  if (!response.ok) {
    throw new Error(response.status === 401 ? 'Please sign in again.' : 'Could not unlink the mobile app.')
  }
  return response.json()
}

export async function fetchPricing(): Promise<PricingInfo | null> {
  try {
    const r = await authedFetch('/api/extension/pricing')
    if (!r.ok) return null
    return await r.json()
  } catch (err) {
    logApiWarning('fetchPricing', err)
    return null
  }
}

export async function fetchSubscriptionStatus(): Promise<SubscriptionStatus> {
  const cached = readCachedSubscription()
  try {
    const r = await authedFetch('/api/extension/subscription-status')
    if (r.status === 401) {
      // Involuntary expiry — keep the remembered email so the user is greeted
      // as a returning customer and routed to sign-in, not the upgrade paywall.
      clearAuthToken({ keepEmail: true })
      const expired: SubscriptionStatus = { status: 'free', plan: null, tier: 'free', authExpired: true }
      writeCachedSubscription(expired)
      return expired
    }
    if (!r.ok) {
      // Drop a stale managed_by from the cache on a failed refresh — a leftover
      // 'mobile' would hide the Stripe portal from a real Stripe customer during
      // an outage. Undefined reads as "unknown" and falls back to the neutral display.
      if (cached) return { ...cached, managed_by: undefined, fetchFailed: true }
      return { status: 'unknown', plan: null, tier: undefined, fetchFailed: true }
    }
    const data = await r.json()
    // Sliding session: the API re-issues a fresh JWT on each authenticated
    // poll. Re-storing it pushes the 14-day cookie/token expiry forward so
    // active paying users are never silently logged out mid-use.
    if (data.refreshed_token) {
      setAuthToken(data.refreshed_token, data.email ?? getAuthEmail() ?? undefined)
    }
    const fresh: SubscriptionStatus = {
      status: (data.status ?? data.subscription_status ?? 'free') as SubscriptionStatus['status'],
      plan: data.plan ?? null,
      is_pro: data.is_pro === true || data.subscription_status === 'active' || data.status === 'active',
      tier: data.tier === 'pro_plus' || data.tier === 'pro' || data.tier === 'free' ? data.tier : undefined,
      email: data.email,
      current_period_end: data.current_period_end,
      managed_by: data.managed_by === 'stripe' || data.managed_by === 'mobile' || data.managed_by === 'none' ? data.managed_by : undefined,
      sources: Array.isArray(data.sources)
        ? data.sources
            .filter((s: unknown): s is Record<string, unknown> => !!s && typeof s === 'object')
            .filter((s: Record<string, unknown>) => s.provider === 'stripe' || s.provider === 'mobile_revenuecat')
            .map((s: Record<string, unknown>) => ({
              provider: s.provider as SubscriptionSource['provider'],
              status: typeof s.status === 'string' ? s.status : 'unknown',
              tier: s.tier === 'pro_plus' || s.tier === 'pro' ? s.tier : 'free',
              current_period_end: typeof s.current_period_end === 'string' ? s.current_period_end : null,
            }))
        : undefined,
    }
    writeCachedSubscription(fresh)
    return fresh
  } catch (err) {
    logApiWarning('fetchSubscriptionStatus', err)
    if (cached) return { ...cached, managed_by: undefined, fetchFailed: true }
    return { status: 'unknown', plan: null, fetchFailed: true }
  }
}

export async function fetchUsage(): Promise<UsageSnapshot | null> {
  try {
    const r = await authedFetch('/api/extension/usage')
    if (!r.ok) return null
    const data = await r.json()
    if (data && (data.daily || data.monthly)) {
      const flat: UsageSnapshot = {
        translationsToday: data.daily?.translations_used ?? 0,
        translationsLimit:
          data.translationsLimit == null
            ? (data.daily?.translations_limit == null
                ? undefined
                : Number(data.daily.translations_limit))
            : Number(data.translationsLimit),
        charsUsed: Number(data.charsUsed ?? data.monthly?.chars_used ?? 0),
        charsLimit:
          data.charsLimit != null
            ? Number(data.charsLimit)
            : data.monthly?.chars_limit != null
              ? Number(data.monthly.chars_limit)
              : undefined,
        charsPerTranslation:
          data.charsPerTranslation != null ? Number(data.charsPerTranslation) : undefined,
        imagesUsed: data.imagesUsed != null ? Number(data.imagesUsed) : undefined,
        imagesLimit: data.imagesLimit != null ? Number(data.imagesLimit) : undefined,
        pdfsUsed: data.pdfsUsed != null ? Number(data.pdfsUsed) : undefined,
        pdfsLimit: data.pdfsLimit != null ? Number(data.pdfsLimit) : undefined,
        period: data.period === 'yearly' ? 'yearly' : 'monthly',
        plan:
          data.plan === 'monthly' || data.plan === 'yearly'
            ? data.plan
            : null,
      }
      return flat
    }
    return data as UsageSnapshot
  } catch (err) {
    logApiWarning('fetchUsage', err)
    return null
  }
}

export interface PromoValidationResult {
  valid: boolean
  discountPercent?: number
  expiresAt?: string
  error?: string
}

export async function validatePromoCode(code: string): Promise<PromoValidationResult> {
  try {
    const r = await authedFetch('/api/extension/validate-code', {
      method: 'POST',
      body: JSON.stringify({ code }),
    })
    const data = await r.json().catch(() => ({}))
    if (data?.valid) {
      return { valid: true, discountPercent: data.discount_percent, expiresAt: data.expires_at }
    }
    return { valid: false, error: data?.error || 'Invalid code' }
  } catch (err) {
    logApiWarning('validatePromoCode', err)
    return { valid: false, error: 'Network error' }
  }
}

export interface PromoRedemptionResult {
  success: boolean
  discountPercent?: number
  message?: string
  error?: string
}

export async function redeemPromoCode(code: string): Promise<PromoRedemptionResult> {
  try {
    const r = await authedFetch('/api/extension/redeem-code', {
      method: 'POST',
      body: JSON.stringify({ code }),
    })
    const data = await r.json().catch(() => ({}))
    if (r.ok && data?.success) {
      return {
        success: true,
        discountPercent: data.discount_percent,
        message: data.message,
      }
    }
    return { success: false, error: data?.error || 'Unable to redeem code' }
  } catch (err) {
    logApiWarning('redeemPromoCode', err)
    return { success: false, error: 'Network error' }
  }
}

export interface HistoryEntry {
  id: string
  source_text: string
  translated_text: string
  source_lang: LangCode
  target_lang: LangCode
  gender?: GenderId
  mode?: ModeId
  model?: ModelId
  created_at: string
}

function sortHistoryEntries(entries: HistoryEntry[]): HistoryEntry[] {
  return [...entries].sort((a, b) => {
    const aTime = Date.parse(a.created_at ?? '')
    const bTime = Date.parse(b.created_at ?? '')
    const aValid = Number.isFinite(aTime)
    const bValid = Number.isFinite(bTime)

    if (aValid && bValid) return bTime - aTime
    if (bValid) return 1
    if (aValid) return -1
    return 0
  })
}

function extractHistoryEntries(data: unknown): HistoryEntry[] {
  if (Array.isArray(data)) return data as HistoryEntry[]
  if (data && typeof data === 'object' && Array.isArray((data as { history?: unknown }).history)) {
    return (data as { history: HistoryEntry[] }).history
  }
  return []
}

function extractHistoryEntry(data: unknown): HistoryEntry | null {
  if (!data || typeof data !== 'object') return null
  if ('id' in data && 'source_text' in data && 'translated_text' in data) return data as HistoryEntry
  const wrapped = data as {
    history?: unknown
    entry?: unknown
    item?: unknown
  }
  if (wrapped.entry && typeof wrapped.entry === 'object') return wrapped.entry as HistoryEntry
  if (wrapped.item && typeof wrapped.item === 'object') return wrapped.item as HistoryEntry
  const history = extractHistoryEntries(wrapped.history ?? null)
  return history[0] ?? null
}

export async function fetchHistory(): Promise<HistoryEntry[]> {
  const r = await authedFetch('/api/extension/history')
  if (!r.ok) throw new Error(`Failed to load history (${r.status})`)
  const data = await r.json()
  return sortHistoryEntries(extractHistoryEntries(data))
}

export async function saveHistoryEntry(entry: Partial<HistoryEntry>): Promise<HistoryEntry> {
  const r = await authedFetch('/api/extension/history', {
    method: 'POST',
    body: JSON.stringify(entry),
  })

  if (r.status === 401) clearAuthToken({ keepEmail: true })
  if (!r.ok) {
    let detail = ''
    try {
      const data = await r.json()
      if (data?.error) detail = `: ${String(data.error)}`
    } catch {}
    throw new Error(`Failed to save history (${r.status})${detail}`)
  }

  try {
    const data = await r.json()
    const saved = extractHistoryEntry(data)
    if (saved) return saved
  } catch {}

  return {
    id: typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `history-${Date.now()}`,
    source_text: entry.source_text ?? '',
    translated_text: entry.translated_text ?? '',
    source_lang: (entry.source_lang ?? 'en') as LangCode,
    target_lang: (entry.target_lang ?? 'he') as LangCode,
    gender: entry.gender,
    mode: entry.mode,
    model: entry.model,
    created_at: new Date().toISOString(),
  }
}

export async function deleteHistoryEntry(id: string): Promise<void> {
  try {
    await authedFetch(`/api/extension/history/${id}`, { method: 'DELETE' })
  } catch (err) {
    logApiWarning('deleteHistoryEntry', err)
  }
}

export interface FavoriteEntry {
  id: string
  source_text: string
  translated_text: string
  source_lang: LangCode
  target_lang: LangCode
  gender?: GenderId
  tags?: string[]
  created_at: string
}

export async function fetchFavorites(): Promise<FavoriteEntry[]> {
  const r = await authedFetch('/api/extension/favorites')
  if (!r.ok) throw new Error(`Failed to load favorites (${r.status})`)
  const data = await r.json()
  if (Array.isArray(data)) return data
  if (data && Array.isArray(data.favorites)) return data.favorites
  return []
}

export async function saveFavorite(entry: Partial<FavoriteEntry>): Promise<void> {
  try {
    await authedFetch('/api/extension/favorites', {
      method: 'POST',
      body: JSON.stringify(entry),
    })
  } catch (err) {
    logApiWarning('saveFavorite', err)
  }
}

export async function deleteFavorite(id: string): Promise<void> {
  try {
    await authedFetch(`/api/extension/favorites/${id}`, { method: 'DELETE' })
  } catch (err) {
    logApiWarning('deleteFavorite', err)
  }
}

export async function createCheckoutSession(params: {
  plan: 'monthly' | 'yearly'
  /** Defaults to 'pro' server-side when omitted. */
  tier?: 'pro' | 'pro_plus'
  successUrl?: string
  cancelUrl?: string
  promoCode?: string
  trialDays?: number
  /** BCP 47 locale tag passed to Stripe Checkout (localizes the form). */
  locale?: string
}): Promise<{
  url?: string
  sessionId?: string
  error?: string
  alreadySubscribed?: boolean
  manageUrl?: string
  authRequired?: boolean
  /** Set when the API sells through Paddle: open Paddle.js on `transactionId` instead of redirecting. */
  provider?: 'paddle'
  transactionId?: string
  email?: string | null
}> {
  try {
    // Pull ad-attribution cookies (gclid/fbclid/UTMs) so Stripe carries the
    // acquisition source on every subscription. Enables revenue-by-ad-group
    // reporting without a database join later.
    const attribution = getClickSignals()
    const consent = getConsentForApi()
    // GA4 identity of this browser, so the Stripe webhook can attribute the
    // purchase to the session/landing page that produced it (see
    // apps/api/src/lib/ga4-mp.ts). Both are optional; the server validates.
    const gaClientId = getGaClientId()
    const gaSessionId = getGaSessionId()
    const r = await authedFetch('/api/extension/create-checkout', {
      method: 'POST',
      body: JSON.stringify({
        plan: params.plan,
        tier: params.tier,
        source: 'app',
        success_url: params.successUrl,
        cancel_url: params.cancelUrl,
        promo_code: params.promoCode,
        trial_days: params.trialDays,
        locale: params.locale,
        attribution: Object.keys(attribution).length ? attribution : undefined,
        consent,
        ga_client_id: gaClientId ?? undefined,
        ga_session_id: gaSessionId ?? undefined,
      }),
    })
    const data = await r.json().catch(() => ({}))
    if (r.status === 401) {
      clearAuthToken({ keepEmail: true })
      return {
        authRequired: true,
        error: data.error || 'Sign in first. Send a magic link to your email and come back.',
      }
    }
    if (r.status === 409 && data?.error === 'already_subscribed') {
      return {
        alreadySubscribed: true,
        manageUrl: data.manage_url,
        error: data.message || 'You already have an active subscription.',
      }
    }
    if (!r.ok) return { error: data.error || 'Checkout failed' }
    // Paddle: the server already created the transaction. There is no Stripe-shaped session to
    // file browser signals under (POST /checkout-signals only accepts Stripe session ids).
    if (data.provider === 'paddle' && typeof data.transactionId === 'string') {
      return { provider: 'paddle', transactionId: data.transactionId, email: data.email ?? null }
    }
    // The session id is the event id the server's InitiateCheckout carries, and the key the
    // browser's Meta and TikTok ids are filed under. Send them before leaving for Stripe.
    const sessionId = checkoutSessionId(data) ?? undefined
    if (sessionId) {
      await authedFetch('/checkout-signals', {
        method: 'POST',
        body: JSON.stringify(checkoutSignalsBody(sessionId, attribution, consent)),
        keepalive: true,
      }).catch((err) => logApiDebug('checkout-signals', err))
    }
    return { url: data.url, sessionId }
  } catch (e) {
    logApiWarning('createCheckoutSession', e)
    return { error: (e as Error).message }
  }
}

/** POST /click-key: the ids behind a click key, for the web-to-app install join. */
export async function postClickKey(body: Record<string, string>): Promise<void> {
  await fetch(`${BABA_API_BASE_URL}/click-key`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    keepalive: true,
    credentials: 'omit',
  })
}

export async function openCustomerPortal(returnUrl?: string): Promise<{ url?: string; error?: string }> {
  try {
    const r = await authedFetch('/api/extension/customer-portal', {
      method: 'POST',
      body: JSON.stringify({ source: 'app', return_url: returnUrl }),
    })
    const data = await r.json().catch(() => ({}))
    if (!r.ok) return { error: data.error || 'Unable to open portal' }
    return { url: data.url }
  } catch (e) {
    logApiWarning('openCustomerPortal', e)
    return { error: (e as Error).message }
  }
}

export async function restoreSubscriptionStatus(): Promise<SubscriptionRestoreResult> {
  try {
    const r = await authedFetch('/api/extension/subscription-status/restore', {
      method: 'POST',
    })
    const data = await r.json().catch(() => ({}))
    if (!r.ok) {
      return {
        restored: false,
        message: data.message,
        error: data.error || data.message || 'Unable to restore subscription',
      }
    }
    return {
      restored: data.restored === true,
      already_active: data.already_active === true,
      plan: data.plan ?? null,
      current_period_end: data.current_period_end,
      message: data.message,
    }
  } catch (e) {
    logApiWarning('restoreSubscriptionStatus', e)
    return { restored: false, error: (e as Error).message }
  }
}

export async function sendMagicLink(params: {
  email: string
  returnUrl?: string
  referralCode?: string | null
}): Promise<{ ok: boolean; error?: string }> {
  try {
    // Paid-click attribution: if the user arrived via a Google Ads click,
    // forward the gclid + UTMs so the API can persist them on the user row
    // for later offline conversion import when Stripe fires `subscription.created`.
    const attr = getClickSignals()
    const consent = getConsentForApi()
    const r = await authedFetch('/api/extension/auth/magic-link', {
      method: 'POST',
      body: JSON.stringify({
        email: params.email,
        source: 'app',
        return_url: params.returnUrl,
        referral_code: params.referralCode ?? undefined,
        gclid: attr.gclid,
        gbraid: attr.gbraid,
        wbraid: attr.wbraid,
        fbclid: attr.fbclid,
        utm_source: attr.utm_source,
        utm_medium: attr.utm_medium,
        utm_campaign: attr.utm_campaign,
        utm_term: attr.utm_term,
        utm_content: attr.utm_content,
        gad_source: attr.gad_source,
        gad_campaignid: attr.gad_campaignid,
        click_ts: attr.click_ts,
        landing_path: attr.landing_path,
        ttclid: attr.ttclid,
        msclkid: attr.msclkid,
        click_key: attr.click_key,
        fbp: attr.fbp,
        fbc: attr.fbc,
        ttp: attr.ttp,
        consent,
      }),
    })
    if (!r.ok) {
      const data = await r.json().catch(() => ({}))
      return { ok: false, error: data.error || 'Failed to send magic link' }
    }
    return { ok: true }
  } catch (e) {
    logApiWarning('sendMagicLink', e)
    return { ok: false, error: (e as Error).message }
  }
}

export async function verifyMagicLink(params: {
  email: string
  token: string
}): Promise<{ token?: string; subscription_status?: string; plan?: string | null; is_new_user?: boolean; eventId?: string; error?: string }> {
  try {
    const r = await authedFetch('/api/extension/auth/verify', {
      method: 'POST',
      body: JSON.stringify(params),
    })
    const data = await r.json().catch(() => ({}))
    if (!r.ok) return { error: data.error || 'Verification failed' }
    return data
  } catch (e) {
    logApiWarning('verifyMagicLink', e)
    return { error: (e as Error).message }
  }
}

export async function verifyLoginCode(params: {
  email: string
  code: string
}): Promise<{ token?: string; subscription_status?: string; plan?: string | null; is_new_user?: boolean; eventId?: string; error?: string; remaining?: number }> {
  try {
    const r = await authedFetch('/api/extension/auth/verify-code', {
      method: 'POST',
      body: JSON.stringify(params),
    })
    const data = await r.json().catch(() => ({}))
    if (!r.ok) return { error: data.error || 'Verification failed', remaining: data.remaining }
    return data
  } catch (e) {
    logApiWarning('verifyLoginCode', e)
    return { error: (e as Error).message }
  }
}

export interface ReferralCodeResponse {
  code: string
  link: string
  invited: number
  proDaysEarned: number
}

export interface AuthSession {
  userId: string
  email: string
  is_pro: boolean
  plan: 'monthly' | 'yearly' | null
  /** 'google' once a Google account is linked, otherwise 'email'. */
  auth_provider: 'google' | 'email'
  /** True when a Google account is linked (Google sign-in works for this account). */
  has_google: boolean
  /** A requested-but-unconfirmed new email address, if any. */
  pending_email: string | null
}

export async function fetchAuthSession(): Promise<AuthSession | null> {
  try {
    const r = await authedFetch('/api/extension/auth/session', { method: 'GET' })
    if (!r.ok) return null
    return (await r.json()) as AuthSession
  } catch (e) {
    logApiWarning('fetchAuthSession', e)
    return null
  }
}

export interface EmailChangeResult {
  success?: boolean
  message?: string
  pending_email?: string
  error?: string
}

export async function requestEmailChange(newEmail: string): Promise<EmailChangeResult> {
  try {
    const r = await authedFetch('/api/extension/auth/change-email/request', {
      method: 'POST',
      body: JSON.stringify({ new_email: newEmail }),
    })
    return (await r.json()) as EmailChangeResult
  } catch (e) {
    logApiWarning('requestEmailChange', e)
    return { error: 'Could not reach the server. Please try again.' }
  }
}

export async function fetchReferralCode(): Promise<ReferralCodeResponse | null> {
  try {
    const r = await authedFetch('/api/extension/referral/code', { method: 'GET' })
    if (!r.ok) return null
    const data = (await r.json()) as ReferralCodeResponse
    return data
  } catch (e) {
    logApiWarning('fetchReferralCode', e)
    return null
  }
}

export interface TranslationVariationsOptions {
  translation: string
  sourceText: string
  sourceLang: LangCode
  targetLang: LangCode
  signal?: AbortSignal
}

export interface TranslationVariationsResult {
  variations: string[]
  proRequired?: boolean
}

export async function fetchTranslationVariations(
  opts: TranslationVariationsOptions,
): Promise<TranslationVariationsResult> {
  try {
    const response = await authedFetch('/api/extension/translation-variations', {
      method: 'POST',
      body: JSON.stringify({
        translation: opts.translation,
        source_text: opts.sourceText,
        source_lang: opts.sourceLang,
        target_lang: opts.targetLang,
      }),
      signal: opts.signal,
    })
    if (response.status === 403) {
      return { variations: [], proRequired: true }
    }
    if (!response.ok) return { variations: [] }
    const data = await response.json().catch(() => ({}))
    const variations = Array.isArray(data.variations)
      ? (data.variations as string[]).filter((v) => typeof v === 'string' && v.trim())
      : []
    return { variations }
  } catch (err) {
    logApiWarning('fetchTranslationVariations', err)
    return { variations: [] }
  }
}

export interface WordAlternativesOptions {
  word: string
  context: string
  targetLang: LangCode
  signal?: AbortSignal
}

export interface WordAlternativesResult {
  alternatives: string[]
}

export async function fetchWordAlternatives(opts: WordAlternativesOptions): Promise<WordAlternativesResult> {
  try {
    const timeoutCtrl = new AbortController()
    const timeoutId = setTimeout(() => timeoutCtrl.abort(), 15_000)
    if (opts.signal) {
      if (opts.signal.aborted) timeoutCtrl.abort()
      else opts.signal.addEventListener('abort', () => timeoutCtrl.abort(), { once: true })
    }

    let response: Response
    try {
      response = await authedFetch('/api/extension/word-alternatives', {
        method: 'POST',
        body: JSON.stringify({
          word: opts.word,
          context: opts.context,
          target_lang: opts.targetLang,
          model: 'openai/gpt-5.5',
        }),
        signal: timeoutCtrl.signal,
      })
    } finally {
      clearTimeout(timeoutId)
    }

    if (!response.ok) return { alternatives: [] }
    const data = await response.json().catch(() => ({}))
    const alternatives = Array.isArray(data.alternatives) ? (data.alternatives as string[]) : []
    return { alternatives }
  } catch (err) {
    logApiWarning('fetchWordAlternatives', err)
    return { alternatives: [] }
  }
}
